import moment from "moment-timezone";
import { renderSettingsResourceLabel } from "utils/helpers/settings";

export type QualificationImpactItem = {
    id: string;
    type: string;
    effect: string;
    startDate: string;
    endDate: string;
};

export type QualificationField = "skills" | "licenses" | "certifications";

type MissingQualificationValue = string | { id?: string; code?: string; description?: string };

export type QualificationImpactShift = {
    scheduleId: string;
    shiftId: string;
    startDate: string;
    endDate: string;
    // Grouped by qualification type; a flat list of types is still accepted.
    missing: Partial<Record<QualificationField, MissingQualificationValue[]>> | string[];
    effect: string;
};

export type QualificationImpact = {
    total: number;
    items: QualificationImpactItem[];
    shifts?: {
        total: number;
        items: QualificationImpactShift[];
    };
};

type DatedQualification = {
    id?: string;
    type?: any;
    status?: string;
    startDate?: any;
    endDate?: any;
};

export const getQualificationImpactUrl = (userId?: string) => `/request/qualifications-impact/${userId}`;
export const getQualificationShiftActionsUrl = (userId?: string) => `/schedules/v2/user/${userId}/resolve-unqualified-shifts`;

export type QualificationShiftAction = "open-shift" | "delete";

export type QualificationShiftActions = Record<string, QualificationShiftAction | undefined>;

export type QualificationShiftActionItem = {
    shiftId: string;
    scheduleId: string;
    action: QualificationShiftAction;
};

export type QualificationShiftPermissions = { canMove: boolean; canDelete: boolean };

// Every affected shift starts as an open shift, falling back to delete when that is the only allowed action.
export const getDefaultShiftActions = (
    shifts: QualificationImpactShift[] = [],
    { canMove, canDelete }: QualificationShiftPermissions,
): QualificationShiftActions => Object.fromEntries(
    shifts.map((shift) => [shift.shiftId, canMove ? "open-shift" : canDelete ? "delete" : undefined]),
);

export const toShiftActionItems = (
    shifts: QualificationImpactShift[] = [],
    actions: QualificationShiftActions,
): QualificationShiftActionItem[] =>
    shifts
        .filter((shift) => actions[shift.shiftId])
        .map(({ shiftId, scheduleId }) => ({ shiftId, scheduleId, action: actions[shiftId]! }));

const getQualificationStatus = (startDate?: string, endDate?: string) => {
    const today = moment().startOf("day");
    if (startDate && moment(startDate).isAfter(today, "day")) return "pending";
    if (endDate && moment(endDate).isBefore(today, "day")) return "expired";
    return "active";
};

const toImpactEntry = ({ id, type, status, startDate, endDate }: DatedQualification) => {
    const start = startDate ? moment(startDate).format("YYYY-MM-DD") : undefined;
    const end = endDate ? moment(endDate).format("YYYY-MM-DD") : undefined;
    return {
        id,
        type: type?.id ?? type,
        status: status ?? getQualificationStatus(start, end),
        startDate: start,
        endDate: end,
    };
};

// The user's full list of licenses/certifications as it will be after saving `changed`.
export const buildQualificationsImpactList = (list: DatedQualification[] = [], changed: DatedQualification) => {
    // Status is recalculated from the new dates rather than trusting the stored one.
    const updated = toImpactEntry({ ...changed, status: undefined });
    return changed.id
        ? list.map((item) => item.id === changed.id ? updated : toImpactEntry(item))
        : [...list.map(toImpactEntry), updated];
};

const qualificationFields: QualificationField[] = ["skills", "licenses", "certifications"];

// One entry per qualification type the shift is missing, with the display names of its values.
export const getMissingQualificationGroups = (missing: QualificationImpactShift["missing"] = []) => {
    if (Array.isArray(missing)) {
        return missing.map((field) => ({ field, values: [] as string[] }));
    }
    return qualificationFields
        .filter((field) => missing[field]?.length)
        .map((field) => ({
            field,
            values: missing[field]!.map((value) => typeof value === "string" ? value : renderSettingsResourceLabel(value)),
        }));
};
